import { useState } from 'react';
import { toast } from 'react-toastify';

import TextEditor from 'components/text-editor';
import { ConfirmacaoDaAcao } from 'components/task-item/modal-actions/moldura-da-acao';
import { useAuth } from 'context/AuthContext';
import { sendNotification } from 'services/notifications.service';
import { stripHtmlTags } from 'utils/helpers';

interface Props {
  onClose: () => void;
  managerId: string;
  managerName: string;
  participantCount?: number;
  taskTitle?: string;
}

/** Pedido do editor de texto; também é o nome acessível do campo. */
const PEDIDO_DE_JUSTIFICATIVA = 'Explique os motivos para essa quantidade de participantes:';

export default function ModalSendMessage({ onClose, managerId, managerName, participantCount, taskTitle }: Props) {
  const [value, setValue] = useState('');
  const [messageLength, setMessageLength] = useState<{ textLength: number; fullLength: number } | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = useAuth();

  const minTextLength = 10;

  function handleChangeMessage(content: string, contentLength: any) {
    setMessageLength(contentLength);
    setValue(content);
  }

  async function handleSendRequest() {
    if (!value || !messageLength || messageLength.textLength < minTextLength) {
      return toast('Você precisa inserir uma justificativa com pelo menos 10 caracteres.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        type: 'error',
      });
    }

    setIsLoading(true);

    try {
      const message = `
        <div>
          <h3><strong>Solicitação de Aprovação de Participantes</strong></h3>
          <p><strong>Solicitante:</strong> ${userInfo?.nome_guerra || 'Não informado'} (${userInfo?.chave || 'Não informado'})</p>
          <p><strong>Realização:</strong> ${taskTitle || 'Não informado'}</p>
          <p><strong>Número de participantes:</strong> ${participantCount || 'Não informado'}</p>
          <p><strong>Justificativa:</strong></p>
          <div>
            ${value}
          </div>
          <br>
          <p><em>Esta solicitação requer sua aprovação para prosseguir com a realização.</em></p>
        </div>
      `;

      const payload = {
        target: managerId,
        message: stripHtmlTags(value),
        created: { $date: new Date().getTime() },
        sender: userInfo?._id || 'Não informado',
        taskTitle: taskTitle || 'Não informado',
        participantCount: participantCount || 0,
        senderName: userInfo?.nome_guerra || 'Não informado',
      };

      await sendNotification(payload);

      toast('Solicitação enviada com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        type: 'success',
      });

      onClose();
    } catch (error) {
      toast('Erro ao enviar solicitação. Tente novamente.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  const justificativaCurta = !value || !messageLength || messageLength.textLength < minTextLength;

  return (
    <ConfirmacaoDaAcao
      title="Solicitar Aprovação de Participantes"
      size="md"
      onClose={onClose}
      onConfirm={handleSendRequest}
      confirmLabel={isLoading ? 'Enviando...' : 'Solicitar Autorização'}
      confirmDisabled={justificativaCurta || isLoading}
    >
      <dl className="m-0 flex flex-col gap-1.5 rounded-jj-md bg-surface-100 px-4 py-3">
        <div>
          <dt className="inline font-bold">Gestor:</dt> <dd className="m-0 inline">{managerName}</dd>
        </div>
        {participantCount && (
          <div>
            <dt className="inline font-bold">Participantes:</dt> <dd className="m-0 inline">{participantCount}</dd>
          </div>
        )}
        {taskTitle && (
          <div>
            <dt className="inline font-bold">Realização:</dt> <dd className="m-0 inline">{taskTitle}</dd>
          </div>
        )}
      </dl>

      <div>
        <p className="m-0 mb-1.5 text-jj-label font-semibold text-content-soft">{PEDIDO_DE_JUSTIFICATIVA}</p>
        <div className="h-[250px]">
          <TextEditor
            appearance="light"
            ariaLabel={PEDIDO_DE_JUSTIFICATIVA}
            onChange={handleChangeMessage}
            minLength={minTextLength}
            disableEmoji={true}
          />
        </div>
      </div>
    </ConfirmacaoDaAcao>
  );
}
